import { formatMinorUnits } from '@seek/currency';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Stack,
  Heading,
  TooltipRenderer,
  IconInfo,
  Button,
  Badge,
  List,
  IconTick,
  Text,
} from 'braid-design-system';
import { useLocation, useNavigate } from 'react-router';

import { useAppContext } from 'src/App/context/AppContext';
import { useTracking } from 'src/App/context/TrackingProvider';

import {
  type AdBudgetSelectionPageBaseEvent,
  createBuyNowPressedEvent,
} from '../../eventTracking/createEvent';
import type { BudgetOption } from '../../mapBudgetOption';

import translations from './.vocab';

interface BudgetPanelProps {
  budgetOption: BudgetOption;
  trackingBaseEvent: AdBudgetSelectionPageBaseEvent;
  mostNumberOfDiscounts: number;
  productSetId: string;
  showBuyNowButton: boolean;
}

export const BudgetPanel = ({
  budgetOption,
  trackingBaseEvent,
  mostNumberOfDiscounts,
  productSetId,
  showBuyNowButton,
}: BudgetPanelProps) => {
  const { t } = useTranslations(translations);
  const { trackLink } = useTracking();

  const { language } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    title,
    budgetId,
    budgetValue,
    currency,
    highlights,
    inclusions,
    tagLine,
    tax,
  } = budgetOption;

  const numberOfDiscounts = highlights.length ?? 0;
  const discountPadding = Array.from(
    { length: mostNumberOfDiscounts - numberOfDiscounts },
    (_, index) => (
      <Box
        key={index}
        marginTop={{ desktop: 'xsmall', tablet: 'none' }}
        marginBottom={{ desktop: 'xsmall', tablet: 'none' }}
      />
    ),
  );

  const budgetDisplayPrice =
    tax?.value && tax.displayRecommendation === 'inclusive'
      ? budgetValue + tax.value
      : budgetValue;

  const isCompactPrice = budgetDisplayPrice.toString().length >= 9;

  let taxDescription = '';

  if (tax?.displayRecommendation === 'inclusive') {
    taxDescription = `${t('Incl.')} ${tax?.code}`;
  }

  if (tax?.displayRecommendation === 'exclusive') {
    taxDescription = `+${tax?.code}`;
  }

  return (
    <Box
      boxShadow="borderNeutralLight"
      borderRadius="xlarge"
      paddingX="gutter"
      paddingY="xlarge"
      background="surface"
      width="full"
    >
      <Stack space="medium">
        <Stack space="small">
          <Heading align="left" level="3">
            {title}
          </Heading>
          <Box>
            <Text tone="secondary" maxLines={2}>
              {tagLine}{' '}
              <TooltipRenderer
                id="adRangeInfo"
                placement="bottom"
                tooltip={<Text>{t('The exact number of ads you...')}</Text>}
              >
                {({ triggerProps }) => (
                  <Box aria-label="Help" {...triggerProps} display="inline">
                    <IconInfo />
                  </Box>
                )}
              </TooltipRenderer>
            </Text>
          </Box>
        </Stack>

        <Stack space="xxsmall">
          {highlights.map((highlight) => (
            <Badge tone="promote" key={highlight}>
              {highlight}
            </Badge>
          ))}
          {discountPadding}
        </Stack>

        <Stack space="small">
          <Heading level={isCompactPrice ? '2' : '1'} align="left">
            {formatMinorUnits(budgetDisplayPrice, {
              currency,
              locale: language,
              dropFraction: true,
            })}
          </Heading>
          <Text size="small" tone="secondary" align="left">
            {taxDescription}
          </Text>
        </Stack>
        {showBuyNowButton && (
          <Button
            tone="formAccent"
            variant="ghost"
            onClick={() => {
              trackLink({
                eventName: 'seek_contract_buy_now_pressed',
                data: {
                  ...trackingBaseEvent,
                  ...createBuyNowPressedEvent(budgetOption),
                },
              });

              const searchParams = new URLSearchParams(location?.search);
              if (!searchParams.has('productId')) {
                searchParams.append('productId', budgetId);
              }

              navigate(
                {
                  pathname: 'checkout',
                  search: searchParams.toString(),
                },
                {
                  state: {
                    pathname: 'products/ad-budgets/checkout',
                    productId: budgetId,
                    productSetId,
                  },
                },
              );
            }}
          >
            {t('Buy Now')}
          </Button>
        )}
        <List type="icon" space="gutter" icon={<IconTick tone="formAccent" />}>
          {inclusions.map((inclusion) => (
            <Text key={inclusion}>{inclusion}</Text>
          ))}
        </List>
      </Stack>
    </Box>
  );
};
